import type { AssetKind, StudioClip, StudioTrack } from './types';

export const PX_PER_SECOND = 120;

export function createId(prefix: string): string {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function formatTime(seconds: number): string {
  const s = Math.max(0, seconds);
  const m = Math.floor(s / 60);
  const rest = s - m * 60;
  const tenths = Math.round((rest % 1) * 10);
  return `${m.toString().padStart(2, '0')}:${Math.floor(rest).toString().padStart(2, '0')}.${tenths}`;
}

export function classifyMimeType(mimeType: string): AssetKind {
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('video/')) return 'video';
  if (mimeType.startsWith('audio/')) return 'audio';
  return 'image';
}

export function classifyFileName(fileName: string, mimeType?: string): AssetKind {
  const kind = classifyMimeType(mimeType ?? '');
  if (kind !== 'image') return kind;
  const ext = fileName.split('.').pop()?.toLowerCase() ?? '';
  if (['mp4', 'webm', 'mov', 'm4v', 'ogv'].includes(ext)) return 'video';
  if (['mp3', 'wav', 'ogg', 'm4a', 'flac', 'aac'].includes(ext)) return 'audio';
  if (['glb', 'gltf'].includes(ext)) return ext === 'glb' ? 'glb' : 'gltf';
  return 'image';
}

export function clipsOverlap(a: StudioClip, b: StudioClip): boolean {
  return a.start < b.start + b.duration && b.start < a.start + a.duration;
}

export function clipEnd(clip: StudioClip): number {
  return clip.start + clip.duration;
}

export function activeClipAt(tracks: StudioTrack[], kind: string, time: number): StudioClip | null {
  for (const track of tracks) {
    if (track.kind !== kind) continue;
    const clip = track.clips.find((c) => time >= c.start && time < clipEnd(c));
    if (clip) return clip;
  }
  return null;
}

export function timelineDuration(tracks: StudioTrack[]): number {
  let max = 0;
  for (const track of tracks) {
    for (const clip of track.clips) {
      max = Math.max(max, clipEnd(clip));
    }
  }
  return max;
}

export function clampClip(clip: StudioClip, track: StudioTrack, minStart = 0): StudioClip {
  let start = Math.max(minStart, clip.start);
  const requestedDuration = Math.max(0.1, clip.duration);
  let duration = requestedDuration;
  let end = start + duration;
  for (const other of track.clips) {
    if (other.id === clip.id) continue;
    if (start < clipEnd(other) && other.start < end) {
      if (start >= other.start) {
        start = clipEnd(other);
        end = start + requestedDuration;
      } else {
        end = other.start;
        duration = Math.max(0.1, end - start);
      }
    }
  }
  return { ...clip, start, duration: Math.max(0.1, end - start) };
}

export function snapTime(time: number, frameRate: number): number {
  const frame = 1 / frameRate;
  return Math.round(time / frame) * frame;
}

export function secondsToPixels(seconds: number): number {
  return seconds * PX_PER_SECOND;
}

export function pixelsToSeconds(pixels: number): number {
  return pixels / PX_PER_SECOND;
}

export function makeDefaultTracks(): StudioTrack[] {
  return [
    { id: createId('track'), kind: 'video', name: 'Video 1', clips: [] },
    { id: createId('track'), kind: 'video', name: 'Video 2', clips: [] },
    { id: createId('track'), kind: 'audio', name: 'Audio 1', clips: [] },
    { id: createId('track'), kind: 'text', name: 'Text 1', clips: [] },
  ];
}
